import React from 'react';
import { EditorState, RichUtils, convertFromRaw, convertToRaw, CompositeDecorator } from 'draft-js';
import Editor, { composeDecorators } from 'draft-js-plugins-editor';
import createVideoPlugin from 'draft-js-video-plugin';
import createLinkPlugin from 'draft-js-anchor-plugin';
import createImagePlugin from 'draft-js-image-plugin';
import LinkAdd from "./link_add";
import MultiDecorator from "draft-js-plugins-editor/lib/Editor/MultiDecorator";
import {Map} from 'immutable';
import DefaultDraftBlockRenderMap from 'draft-js/lib/DefaultDraftBlockRenderMap';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import GalleryBrowserComponent from "./gallery/browser";
import BaseComponent from "../base-component";
import createIdoaVideoPlugin from './idoa-video-plugin';

const Link = (props) => {
    const {url, target} = props.contentState.getEntity(props.entityKey).getData();
    return (
        <a href={url} target={target || '_blank'}>
            {props.children}
        </a>
    );
};

const videoPlugin = createVideoPlugin();
const linkPlugin = createLinkPlugin({ Link: Link });
const idoaVideoPlugin = createIdoaVideoPlugin();

// const alignmentPlugin = createAlignmentPlugin();
// const resizeablePlugin = createResizeablePlugin();
// const focusPlugin = createFocusPlugin();

// const decorator = composeDecorators(
//     resizeablePlugin.decorator,
//     alignmentPlugin.decorator,
//     focusPlugin.decorator
// );

class ImageComponent extends BaseComponent {
    render() {
        const {block, contentState} = this.props;
        const data = contentState.getEntity(block.getEntityAt(0)).getData();
        return (<img src={data.src} alt={data.alt} className={data.className}/>);
    }
}

const imagePlugin = createImagePlugin({
    imageComponent: ImageComponent
});

export const plugins = [
    linkPlugin,
    videoPlugin,
    idoaVideoPlugin,
    imagePlugin
];

function getPluginDecoratorArray() {
    let decorators = [];
    let plugin;

    for (plugin of plugins) {
        if (plugin.decorators !== null && plugin.decorators !== undefined) {
            decorators = decorators.concat(plugin.decorators);
        }
    }
    return decorators;
}

export const contentEditorDecorator = () => {
    return new MultiDecorator([new CompositeDecorator(getPluginDecoratorArray())]);
};

const blockRenderMap = Map({
    'code-block': {
        element: 'code',
        wrapper: DefaultDraftBlockRenderMap.get('code-block')['wrapper'],
    }
});

export const processContentToState = (propsContent) => {
    let decorator = contentEditorDecorator();
    let modelContent = null;

    if (propsContent && propsContent.length > 0) {
        try {
            let parsedContent = JSON.parse(propsContent);
            modelContent = EditorState.createWithContent(convertFromRaw(parsedContent), contentEditorDecorator());
        } catch(e) {
            modelContent = EditorState.createEmpty(contentEditorDecorator());
        }
    } else {
        modelContent = EditorState.createEmpty(contentEditorDecorator());
    }

    return modelContent;
};

export class ContentEditor extends React.Component {
    onChange = editorState => {
        this.props.onChange(this.props.id, editorState);
    };

    focus = () => this.refs.editor.focus();

    handleKeyCommand = command => {
        const { editorState } = this.props;
        const newState = RichUtils.handleKeyCommand(editorState, command);
        if (newState) {
            this.onChange(newState);
            return true;
        }
        return false;
    };

    onTab = e => {
        const maxDepth = 4;
        this.onChange(RichUtils.onTab(e, this.props.editorState, maxDepth));
    };
    toggleBlockType = blockType => {
        this.onChange(RichUtils.toggleBlockType(this.props.editorState, blockType));
    };
    toggleInlineStyle = inlineStyle => {
        this.onChange(
            RichUtils.toggleInlineStyle(this.props.editorState, inlineStyle)
        );
    };
    render() {
        const { editorState } = this.props;

        let className = 'RichEditor-editor';
        let contentState = editorState.getCurrentContent();
        if (!contentState.hasText()) {
            if (contentState.getBlockMap().first().getType() !== 'unstyled') {
                className += ' RichEditor-hidePlaceholder';
            }
        }

        return (
            <div className="RichEditor-root content-unit-content">
                <BlockStyleControls
                    editorState={editorState}
                    onToggle={this.toggleBlockType}
                    onChange={this.onChange}
                />
                <InlineStyleControls
                    editorState={editorState}
                    onToggle={this.toggleInlineStyle}
                    onChange={this.onChange}
                />
                <div className={className} onClick={this.focus}>
                    <Editor
                        blockRenderMap={blockRenderMap}
                        blockStyleFn={getBlockStyle}
                        customStyleMap={styleMap}
                        editorState={editorState}
                        handleKeyCommand={this.handleKeyCommand}
                        onChange={this.onChange}
                        onTab={this.onTab}
                        placeholder=""
                        ref="editor"
                        spellCheck={true}
                        plugins={plugins}
                    />
                </div>
            </div>
        );
    }
}

// Custom overrides for "code" style.
const styleMap = {
    CODE: {
        backgroundColor: 'rgba(0, 0, 0, 0.05)',
        fontFamily: '"Inconsolata", "Menlo", "Consolas", monospace',
        fontSize: '1rem',
        padding: 2,
    }
};

function getBlockStyle(block) {
    switch (block.getType()) {
        case 'align-left':
            return 'align-left';
        case 'align-center':
            return 'align-center';
        case 'align-right':
            return 'align-right';
        default:
            return null;
    }
}

class StyleButton extends React.Component {
    constructor(params) {
        super(params);
        this.onToggle = e => {
            e.preventDefault();
            this.props.onToggle(this.props.style);
        };
    }
    render() {
        const { icon, label, active } = this.props;

        let className = 'RichEditor-styleButton';
        if (active) className += ' RichEditor-activeButton';

        return (<span className={className} onMouseDown={this.onToggle}>{ !icon && label } { icon && <FontAwesomeIcon icon={icon} className='mr-2'/> }</span>);
    }
}

const BLOCK_TYPES = [
    { label: 'H1', style: 'header-one', icon: ['far', 'h1'] },
    { label: 'H2', style: 'header-two', icon: ['far', 'h2'] },
    { label: 'H3', style: 'header-three', icon: ['far', 'h3'] },
    { label: 'Blockquote', style: 'blockquote', icon: ['far', 'quote-right'] },
    { label: 'UL', style: 'unordered-list-item', icon: ['far', 'list-ul'] },
    { label: 'OL', style: 'ordered-list-item', icon: ['far', 'list-ol'] },
    { label: 'Code Block', style: 'code-block', icon: ['far', 'brackets'] },
    { label: 'Align left', style: 'align-left', icon: ['far', 'align-left'] },
    { label: 'Align center', style: 'align-center', icon: ['far', 'align-center'] },
    { label: 'Align right', style: 'align-right', icon: ['far', 'align-right'] },
];

const BlockStyleControls = props => {
    const { editorState, onChange } = props;
    const selection = editorState.getSelection();
    const blockType = editorState
        .getCurrentContent()
        .getBlockForKey(selection.getStartKey())
        .getType();
    return (
        <div className="RichEditor-controls">
            {BLOCK_TYPES.map(type =>
                <StyleButton
                    key={type.label}
                    active={type.style === blockType}
                    label={type.label}
                    onToggle={props.onToggle}
                    style={type.style}
                    icon={type.icon}
                />
            )}
            {/*<VideoAdd*/}
            {/*    className='RichEditor-styleButton'*/}
            {/*    editorState={editorState}*/}
            {/*    onChange={onChange}*/}
            {/*    modifier={videoPlugin.addVideo}/>*/}
            <GalleryBrowserComponent
                className='RichEditor-styleButton'
                editorState={editorState}
                videoPlugin={videoPlugin}
                onChange={onChange}/>
        </div>
    );
};

const INLINE_STYLES = [
    { label: 'Bold', style: 'BOLD', icon: ['far', 'bold'] },
    { label: 'Italic', style: 'ITALIC', icon: ['far', 'italic'] },
    { label: 'Underline', style: 'UNDERLINE', icon: ['far', 'underline'] },
    { label: 'Monospace', style: 'CODE', icon: ['far', 'code'] },
];

const InlineStyleControls = props => {
    const { editorState, onChange } = props;
    var currentStyle = props.editorState.getCurrentInlineStyle();
    const selection = editorState.getSelection();
    const blockType = editorState
        .getCurrentContent()
        .getBlockForKey(selection.getStartKey())
        .getType();

    return (
        <div className="RichEditor-controls">
            {INLINE_STYLES.map(type =>
                <StyleButton
                    key={type.label}
                    active={currentStyle.has(type.style)}
                    label={type.label}
                    onToggle={props.onToggle}
                    style={type.style}
                    icon={type.icon}
                />
            )}
            <LinkAdd
                editorState={editorState}
                onChange={onChange}
                active={'LINK' === blockType}
                className='RichEditor-styleButton'
                toggle={props.onToggle}
            />
        </div>
    );
};
